<script lang="ts" generics="TValues, TValue, TOption extends __UnknownDictionary">
    import Select from 'svelte-select';
    import { onMount } from 'svelte';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import Spinner from '$components/Spinner/Spinner.svelte';
    import type { KeyOfWithGuard } from '$lib/utils/types.js';
    import { cx } from '$lib/utils/cx';
    import type { ComputeConfig } from 'svelte-floating-ui';
    import { flip, offset, shift } from '@floating-ui/core';
    import {
        wrapSvelteSelectDefaultClass,
        createSizeMiddleware,
    } from '$components/Form/Select.svelte';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue | null>;
    export let placeholder: string = '';

    export let defaultClass: string = wrapSvelteSelectDefaultClass;
    let className: string = '';
    export { className as class };

    export let defaultItemClass: string = cx(
        'px-[13px] py-[10px]',
        //
        '[.svelte-select-xs_&]:text-12',
        //
        '[.svelte-select-sm_&]:px-[11px] [.svelte-select-sm_&]:py-[8px]',
        '[.svelte-select-sm_&]:text-14',
        //
    );

    export let showChevron = false;

    export let label: KeyOfWithGuard<TOption, string | number | null> & string;
    export let optionId: KeyOfWithGuard<TOption, string | number> & string;
    export let id: string | null = null;

    export let loadOptions: (q: string) => Promise<TOption[]>;

    export let optionToValue: (option: TOption) => TValue;
    export let valueToOption: (option: TValue) => Promise<TOption | null>;
    export let valueToId: (value: TValue) => string | number;

    let mounted = false;
    onMount(() => {
        mounted = true;
    });

    async function _loadOptions(q: string): Promise<TOption[]> {
        if (mounted === false) {
            return [];
        }

        return loadOptions(q);
    }

    let _value = form.stores().fieldValue(field);
    let value = $_value;
    let option: TOption | null = null;

    let wrapEl: HTMLElement | null = null;
    const floatingConfig: ComputeConfig = {
        strategy: 'absolute',
        placement: 'bottom-start',
        middleware: [offset(5), createSizeMiddleware(() => wrapEl), flip(), shift()],
        autoUpdate: true,
    };

    function onBlur(_event: CustomEvent<unknown>) {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus(_event: CustomEvent<unknown>) {
        form.dispatch(form.actions().focus(field));
    }

    function onChange(event: CustomEvent<TOption>) {
        value = optionToValue(event.detail);
        option = event.detail;
        form.dispatch(form.actions().changeValue(value, field));
    }

    function onClear(_event: CustomEvent<TOption>) {
        value = null;
        option = null;
        form.dispatch(form.actions().changeValue(null, field));
    }

    async function syncV(mounted: boolean, __v: unknown, __option: unknown) {
        if (mounted === false) {
            return;
        }

        if (value == null) {
            option = null;
        } else if (
            value != null &&
            (option == null || option[optionId] !== valueToId(value))
        ) {
            option = (await valueToOption(value)) ?? null;
        }
    }

    $: _value = form.stores().fieldValue(field);
    $: value = $_value;
    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasAnyErrors = form.stores().hasFieldAnyErrors(field);
    $: effectiveId = id ?? form.getFieldId(field);

    $: syncV(mounted, value, option);

    interface $$Slots {
        prepend: Record<string, never>;
        selection: {
            selection: TOption;
        };
        'list-prepend': Record<string, never>;
        'list-append': Record<string, never>;
        option: {
            option: TOption;
            index: number;
        };
        empty: Record<string, never>;
        'input-hidden': { value: TValue | null };
        required: Record<string, never>;
    }
</script>

<div
    class={cx(defaultClass, className)}
    bind:this={wrapEl}
    class:is-invalid={($nbSubmits > 0 || $isAlreadyBlur) && $hasAnyErrors}
>
    <Select
        class="svelte-select"
        loadOptions={_loadOptions}
        id={effectiveId}
        value={option}
        {label}
        itemId={optionId}
        on:blur={onBlur}
        on:focus={onFocus}
        on:change={onChange}
        on:clear={onClear}
        hasError={$hasAnyErrors && $nbSubmits >= 1 && $isAlreadyBlur}
        {placeholder}
        {showChevron}
        {floatingConfig}
    >
        <slot name="prepend" slot="prepend" />
        <slot name="selection" slot="selection" let:selection {selection}
            >{selection[label] ?? ''}</slot
        >
        <svelte:fragment slot="loading-icon">
            <Spinner size="sm" />
        </svelte:fragment>
        <slot name="list-prepend" slot="list-prepend" />
        <slot name="list-append" slot="list-append" />
        <slot name="option" slot="item" let:item let:index {index} option={item}>
            <div class={defaultItemClass}>{item?.[label] ?? ''}</div>
        </slot>
        <slot name="empty" slot="empty" />
        <slot name="input-hidden" slot="input-hidden" let:value {value} />
        <slot name="required" slot="required" />
    </Select>
</div>
